/**
 * UPE-3 / UPE-4 — tier-aware Coverage Matrix cell status.
 *
 * The legacy `computeSufficiency` answers "how thick is this one
 * doc?" — a per-doc chip signal based on `textContent.length` +
 * DCT match score. It says NOTHING about whether the doc's
 * extraction actually populated any SED slot. Cliff 1 in the UPE
 * pack: a 2KB doc with score 0.71 reads "Strong" while the slot it
 * maps to stays `'unavailable'`.
 *
 * `computeRequirementStatus` replaces the cell-level decision with a
 * three-tier walk:
 *
 *   Tier 1 — AUTHORED EVIDENCE
 *     User typed the requirement's structured fields via the
 *     `[Author]` dialog (UAE-2). Status is derived from field
 *     completeness against `authoredSchema` (not persisted confidence):
 *       - all required + ≥80% structured → `'authored-strong'`
 *       - required all OR ≥50% structured → `'authored'`
 *       - any field / prose notes          → `'tentative'`
 *
 *   Tier 2 — SED SLOT POPULATED BY DOC EXTRACTION
 *     Walks `REQUIREMENT_TO_SECTION_MAP` for section population AND
 *     `REQUIREMENT_TO_FIELDS` for field completeness:
 *       - all sections + ≥80% fields     → `'strong'`
 *       - ≥50% fields OR half+ sections  → `'adequate'`
 *       - any field / section signal     → `'tentative'`
 *       - section-only (no field registry) capped at `'adequate'`
 *       - none populated                 → fall through to Tier 3
 *
 *   Tier 3 — DCT MATCH FALLBACK
 *     The legacy doc-match-score-only heuristic. Capped at
 *     `'tentative'` (DCT alone is no longer "strong" — the user
 *     uploaded a doc the system thinks fits, but extraction either
 *     failed or hasn't run yet). Floor: `'missing'`.
 *
 * Result: the user only sees `'strong'` when the data they care
 * about actually exists on the SED. False positives (cell says
 * "Strong", slot stays empty) cannot happen.
 *
 * KEY INVARIANT — every surface that shows a requirement's coverage
 * status (e.g. the Site-Evidence `CoverageMatrix` and the Path-to-GO
 * gap rows) MUST derive it from this function with the same inputs so
 * no two surfaces can disagree. The W66 bug (the since-removed
 * Documents-tab rail showed 5 missing while Site-Evidence showed 1
 * authored) was caused by two surfaces using different data sources.
 * UPE-4 fixed it by routing both through here.
 */
import { isTariffRequirement } from './resolveReportFields';
import { scopeGridRequirementEvidence } from './scopeGridRequirementEvidence';
import { resolveRequirementFields } from '../evidenceLineage/requirementToFields';
import type { SiteDocument } from '../../types/siteRag';
import { evidenceProbeFor } from './evidenceProbes';
import type {
  EvidenceSection,
  SedSectionId,
  SiteEvidenceDocument,
} from '../../types/siteEvidence';
import type { ExtractedClaim } from '../../types/siteContext';
import type {
  ChecklistItem,
  RankedChecklistMatch,
} from '../../config/documentChecklists';
import type { ProjectIntent, TypedPreferencesByType } from '../../types/siteRag';
import type { ReportTypeId } from '../../config/reportTypes';
import { REQUIREMENT_TO_SECTION_MAP } from '../../config/requirementToSection';
import {
  evaluateAuthoredCompleteness,
  evaluateSedFieldCompleteness,
} from './evaluateRequirementCompleteness';
import {
  capRequirementStatusForConflicts,
  unresolvedConflictsForRequirement,
} from './requirementConflictCaps';
import {
  evaluateSetupRequirementCompleteness,
  resolveReportSetupFields,
} from './resolveReportSetupFields';
import { isNonDocumentRequirement } from '../../config/reportSetupRequirements';
// SED D0.3 — the Tier-3 DCT-fallback floor and the extraction-attempt
// floor are the SAME "is this a match?" axis; import the single source
// of truth so they cannot drift apart. It lives in a dependency-free lib
// module, so this pure lib no longer reaches into the scorer service.
import { EXTRACTION_RELEVANCE_FLOOR } from '../docMatching/relevanceThresholds';

export type RequirementStatus =
  | 'authored-strong'
  | 'authored'
  | 'strong'
  | 'adequate'
  | 'tentative'
  | 'missing';

/**
 * Tier 3 (DCT fallback) — score above which a doc-match qualifies
 * as `'tentative'`. Below this floor the cell reads `'missing'`.
 * Matches the existing `CoverageMatrix.resolveCell` 0.40 floor so
 * the fallback band is unchanged.
 *
 * SED D0.3 — aliased to `EXTRACTION_RELEVANCE_FLOOR` (= 0.40) so the
 * DCT-tentative floor and the extraction-attempt floor cannot drift
 * apart. Numerically identical to the prior local 0.40 literal.
 */
const DCT_TENTATIVE_FLOOR = EXTRACTION_RELEVANCE_FLOOR;

/** Inputs needed to resolve one requirement's cell status. */
export interface ComputeRequirementStatusInput {
  /** The checklist row being resolved. */
  requirement: ChecklistItem;
  /** The live SED, or undefined when bootstrap hasn't landed yet. */
  sed?: SiteEvidenceDocument;
  /** The full site documents array (used to verify slot→doc linkage). */
  documents: ReadonlyArray<SiteDocument>;
  /** Authored claims off `site.siteContext?.claims ?? []`. */
  authoredClaims?: ReadonlyArray<ExtractedClaim>;
  /** Optional DCT match for the report-type cell — used only for Tier 3 fallback. */
  match?: RankedChecklistMatch;
  /** Site project intent — setup / intent row resolution. */
  projectIntent?: ProjectIntent;
  /** Site typed preference defaults — setup row resolution. */
  typedPreferences?: TypedPreferencesByType;
  /** Report type in scope for per-type setup rows. */
  reportType?: ReportTypeId;
}

/**
 * SED D0.3 — true when a slot's `data` payload carries no usable
 * field (null/undefined, empty object, or an object whose every value
 * is null/undefined/empty-string). Used to decide whether an
 * `extractionAttempt` left the slot genuinely empty.
 */
function isEmptyData(data: unknown): boolean {
  if (data == null) return true;
  if (typeof data !== 'object') return false;
  const entries = Object.values(data as Record<string, unknown>);
  if (entries.length === 0) return true;
  return entries.every(v =>
    v == null || (typeof v === 'string' && v.trim() === ''),
  );
}

/**
 * SED D0.3 — return the structured "matched but not extracted" reason
 * for a slot, or undefined when the slot was populated / no attempt was
 * recorded. Lets the Coverage Matrix chip render
 * "attempted & rejected: <reason>" instead of a silent empty cell.
 */
export function slotExtractionRejection<T>(
  slot: EvidenceSection<T> | undefined,
): NonNullable<EvidenceSection<T>['extractionAttempt']> | undefined {
  if (!slot?.extractionAttempt?.attempted) return undefined;
  if (slot.data != null && !isEmptyData(slot.data)) return undefined;
  return slot.extractionAttempt;
}

/**
 * True when the SED slot for `sectionId` has been populated by a doc
 * (status is verified-from-doc OR linkedDocumentIds is non-empty
 * AND status is not 'unavailable'). The latter catches authored /
 * provider+doc paths.
 */
function isSlotPopulatedByDoc<T>(slot: EvidenceSection<T> | undefined): boolean {
  if (!slot) return false;
  if (slot.status === 'unavailable') return false;
  // SED D0.3 — a slot whose doc extraction was ATTEMPTED but rejected
  // (matched doc, nothing landed) carries `extractionAttempt.attempted`
  // and a still-empty `data`. It must NOT count as populated — the
  // matched doc supplied no field. The reason is retrievable via
  // `slotExtractionRejection` so the Coverage Matrix can show WHY.
  if (slot.extractionAttempt?.attempted && (slot.data == null || isEmptyData(slot.data))) {
    return false;
  }
  const linkedIds = slot.linkedDocumentIds ?? [];
  // Slots can be populated by provider + boosted by a doc, or
  // verified-from-doc, or have linkedDocumentRefs only. Any one
  // signal that a doc participated counts.
  if (linkedIds.length > 0) return true;
  if ((slot.linkedDocumentRefs ?? []).length > 0) return true;
  if (slot.status === 'verified-from-doc') return true;
  // CAF (2026-05-18) — DCO-stamped tier statuses also count as Tier 2
  // population. The orchestrator writes a non-null `data` payload
  // before flipping the status to `'verified-from-dam'` or
  // `'verified-from-research'`, so a row whose underlying SED slot
  // carries one of these statuses MUST read above 'missing' on the
  // Coverage Matrix. Without this branch the customer sees "5/5
  // missing" copy on a SED the orchestrator filled — the exact
  // regression the CAF pack exists to fix.
  if (slot.status === 'verified-from-dam') return true;
  if (slot.status === 'verified-from-research') return true;
  // Verified-from-context (authored evidence) does NOT count as
  // doc-populated for Tier 2 — Tier 1 handles authored claims
  // separately so we don't double-count.
  return false;
}

/**
 * Returns the (section, slot) tuple from `sed` at `sectionId`, typed
 * as the minimal `EvidenceSection<unknown>` view so callers don't
 * need to discriminate the section union.
 */
function getSlot(
  sed: SiteEvidenceDocument | undefined,
  sectionId: SedSectionId,
): EvidenceSection<unknown> | undefined {
  if (!sed) return undefined;
  const all = sed as unknown as Record<string, EvidenceSection<unknown> | undefined>;
  return all[sectionId];
}

/**
 * Three-tier walk — see module docstring.
 */
export function computeRequirementStatus(
  input: ComputeRequirementStatusInput,
): RequirementStatus {
  const { requirement, sed, authoredClaims, match, projectIntent, typedPreferences, reportType } = input;
  const hasConflicts = unresolvedConflictsForRequirement(sed, requirement.id).length > 0;
  const finish = (status: RequirementStatus): RequirementStatus =>
    capRequirementStatusForConflicts(status, hasConflicts);

  // ── Tier 0 — report setup + Site Project Intent ────────────────
  // P3.8 follow-through — evidence-probed rows: the SED's own scan decides,
  // before any authored, extracted or document tier is consulted.
  const evidenceProbe = evidenceProbeFor(requirement);
  if (evidenceProbe) {
    return finish(evidenceProbe(sed).status);
  }

  if (isNonDocumentRequirement(requirement)) {
    const resolved = resolveReportSetupFields({
      requirement,
      reportType,
      projectIntent,
      typedPreferences,
    });
    const setupCompleteness = evaluateSetupRequirementCompleteness(
      requirement,
      resolved,
      reportType,
      typedPreferences,
      projectIntent,
    );
    const hasExplicitSitePrefs =
      requirement.kind === 'report-setup'
      && reportType
      && typedPreferences?.[reportType] != null;

    if (setupCompleteness.meetsStrongBar) {
      return finish(hasExplicitSitePrefs ? 'authored-strong' : 'strong');
    }
    if (setupCompleteness.meetsAdequateBar) {
      return finish(hasExplicitSitePrefs ? 'authored' : 'adequate');
    }
    if (setupCompleteness.meetsTentativeBar) return finish('tentative');
    return finish('missing');
  }

  // ── Tier 1 — authored evidence ─────────────────────────────────
  // A user-typed claim against this requirement is the strongest
  // signal (the user themselves vouches for the data).
  const authoredForReq = (authoredClaims ?? []).filter(
    c => c.requirementId === requirement.id,
  );
  if (authoredForReq.length > 0) {
    const authoredCompleteness = evaluateAuthoredCompleteness(
      requirement,
      authoredForReq,
    );
    if (authoredCompleteness.meetsStrongBar) return finish('authored-strong');
    if (authoredCompleteness.meetsAdequateBar) return finish('authored');
    if (authoredCompleteness.meetsTentativeBar) return finish('tentative');
    return finish('authored');
  }

  // ── Tier 2 — SED slot populated by doc extraction ──────────────
  // Walk the SED sections this requirement is supposed to fill. The
  // map is exhaustive for every requirement the platform surfaces;
  // a requirement absent from the map falls through to Tier 3.
  const sectionIds = REQUIREMENT_TO_SECTION_MAP[requirement.id];
  if (sectionIds && sectionIds.length > 0 && sed) {
    const scopedGridRequirement = requirement.id === 'utility-letter' || requirement.id === 'iso-queue';
    const evidenceSed = scopedGridRequirement
      ? scopeGridRequirementEvidence({ requirement, sed, documents: input.documents, match }) : sed;
    let populated = 0;
    for (const sid of sectionIds) {
      if (isSlotPopulatedByDoc(getSlot(evidenceSed, sid))) populated += 1;
    }
    const fieldCompleteness = evaluateSedFieldCompleteness(requirement.id, sed, { requirement, documents: input.documents, match });
    const hasRegionalContext = scopedGridRequirement && (
      resolveRequirementFields(sed, requirement.id, true).some(field => !field.isEmpty)
      || sectionIds.some(sid => isSlotPopulatedByDoc(getSlot(sed, sid)))
    );
    const halfOrMoreSections =
      populated > 0 && populated * 2 >= sectionIds.length;
    // Regional DAM/research context may populate grid/electricity without
    // providing the applicable utility letter or tariff fields.
    const requiresRequirementFields = isTariffRequirement(requirement.id)
      || requirement.id === 'utility-letter' || requirement.id === 'iso-queue';

    if (fieldCompleteness.hasFieldRegistry) {
      if (
        fieldCompleteness.meetsStrongBar &&
        populated === sectionIds.length
      ) {
        return finish('strong');
      }
      if (fieldCompleteness.meetsAdequateBar || (!requiresRequirementFields && halfOrMoreSections)) {
        return finish('adequate');
      }
      if (fieldCompleteness.meetsTentativeBar || populated > 0 || hasRegionalContext) {
        return finish('tentative');
      }
    } else {
      if (!requiresRequirementFields && populated === sectionIds.length) return finish('adequate');
      if (!requiresRequirementFields && halfOrMoreSections) return finish('adequate');
      if (populated > 0) return finish('tentative');
    }
    // 0 of N populated — fall through to Tier 3.
  }

  // ── Tier 3 — DCT match fallback ────────────────────────────────
  // The legacy doc-match-score-only path. Capped at `'tentative'`
  // because a high DCT score WITHOUT slot population is what
  // produced the "lies about Strong" cliff (UPE Cliff 1).
  if (match) {
    const rows = match.matched.get(requirement.id);
    if (rows && rows.length > 0) {
      const top = rows[0]!;
      if (top.score >= DCT_TENTATIVE_FLOOR) return finish('tentative');
    }
  }

  return finish('missing');
}

/**
 * Aggregate `computeRequirementStatus` results across every report
 * type in scope, returning the best status across the union. Used
 * by surfaces that present one chip per requirement (the right-rail
 * Coverage panel, the Best-Status column on the matrix).
 */
export function computeRequirementStatusAcrossTypes(args: {
  requirement: ChecklistItem;
  sed?: SiteEvidenceDocument;
  documents: ReadonlyArray<SiteDocument>;
  authoredClaims?: ReadonlyArray<ExtractedClaim>;
  matchByType: ReadonlyMap<string, RankedChecklistMatch>;
  projectIntent?: ProjectIntent;
  typedPreferences?: TypedPreferencesByType;
}): RequirementStatus {
  const { requirement, sed, documents, authoredClaims, matchByType, projectIntent, typedPreferences } = args;
  const STATUS_RANK: Record<RequirementStatus, number> = {
    'authored-strong': 5,
    'strong': 4,
    'adequate': 3,
    'authored': 2,
    'tentative': 1,
    'missing': 0,
  };
  let best: RequirementStatus = 'missing';
  // Tier 1+2 are report-type-agnostic — only Tier 3 depends on the
  // per-type match. So we run the function ONCE with no match to
  // resolve Tier 1+2, then sweep the per-type matches for the Tier 3
  // upgrade path.
  const baseline = computeRequirementStatus({
    requirement,
    sed,
    documents,
    authoredClaims,
    projectIntent,
    typedPreferences,
  });
  if (STATUS_RANK[baseline] > STATUS_RANK[best]) best = baseline;
  for (const [typeId, match] of matchByType.entries()) {
    const status = computeRequirementStatus({
      requirement,
      sed,
      documents,
      authoredClaims,
      match,
      projectIntent,
      typedPreferences,
      reportType: typeId as ReportTypeId,
    });
    if (STATUS_RANK[status] > STATUS_RANK[best]) best = status;
  }
  return best;
}
